import { Builder } from '../core/Builder';
import { Contact, Description, ImageSource, Provider } from '../types';
import { LocationDates } from '../types/LocationDates';
import { BalloonEventModel } from './BalloonEventModel';
import { CategoryModel } from './CategoryModel';
import { DescriptionModel } from './DescriptionModel';
import { hashCode } from '../utils/hash';
import { ContentType, DescriptionType } from '../enums';
import { Organizer } from '../types/Organizer';

export class EventCollectionModelDraftBuilder extends Builder<EventCollectionModel> {
  private _name: string;
  private _type: string;
  private _description: string;
  private _events: BalloonEventModel[];

  constructor() {
    super();
    this._name = '';
    this._type = '';
    this._description = '';
    this._events = [];
  }

  public clear(): EventCollectionModelDraftBuilder {
    this._name = '';
    this._type = '';
    this._description = '';
    this._events = [];
    return this;
  }

  public name(name: string): EventCollectionModelDraftBuilder {
    this._name = name;
    return this;
  }

  public type(type: string): EventCollectionModelDraftBuilder {
    this._type = type;
    return this;
  }

  public description(description: string): EventCollectionModelDraftBuilder {
    this._description = description;
    return this;
  }

  public events(events: BalloonEventModel[]): EventCollectionModelDraftBuilder {
    this._events = events;
    return this;
  }

  public event(event: BalloonEventModel): EventCollectionModelDraftBuilder {
    this._events.push(event);
    return this;
  }

  build(): EventCollectionModel {
    return new EventCollectionModel(
      '',
      this._name,
      '',
      this._type!,
      this._description,
      this._events
    );
  }
}

export class EventCollectionModelBuilder extends Builder<EventCollectionModel> {
  private _id: string;
  private _name: string;
  private _slug: string;
  private _type: string;
  private _description: string;
  private _events: BalloonEventModel[];

  constructor() {
    super();
    this._id = '';
    this._name = '';
    this._slug = '';
    this._type = '';
    this._description = '';
    this._events = [];
  }

  public id(id: string): EventCollectionModelBuilder {
    this._id = id;
    return this;
  }

  public name(name: string): EventCollectionModelBuilder {
    this._name = name;
    return this;
  }

  public slug(slug: string): EventCollectionModelBuilder {
    this._slug = slug;
    return this;
  }

  public type(type: string): EventCollectionModelBuilder {
    this._type = type;
    return this;
  }

  public description(description: string): EventCollectionModelBuilder {
    this._description = description;
    return this;
  }

  public events(events: BalloonEventModel[]): EventCollectionModelBuilder {
    this._events = events;
    return this;
  }

  build(): EventCollectionModel {
    return new EventCollectionModel(
      this._id,
      this._name,
      this._slug,
      this._type!,
      this._description,
      this._events
    );
  }
}

export class EventCollectionModel {
  private _id: string;
  private _name: string;
  private _slug: string;

  private _type: string;

  private _collectionDescription: string;
  private _events: BalloonEventModel[];

  constructor(
    id: string,
    name: string,
    slug: string,
    type: string,
    description: string,
    events: BalloonEventModel[]
  ) {
    this._id = id;
    this._name = name;
    this._slug = slug;
    this._type = type;
    this._collectionDescription = description;
    this._events = events;
  }

  public get id(): string {
    return this._id;
  }

  public get name(): string {
    return this._name;
  }

  public get slug(): string {
    return this._slug;
  }

  public get type(): string {
    return this._type;
  }

  public get collectionDescription(): string {
    return this._collectionDescription;
  }

  public get teaser() {
    if (this._events.length === 0) return '';
    return this._events[0].teaser;
  }

  public get description() {
    if (this._events.length === 0) return '';
    return this._events[0].description;
  }

  public get events(): BalloonEventModel[] {
    return this._events;
  }

  public static from(data: EventCollectionModel): EventCollectionModel {
    return new EventCollectionModel(
      data.id,
      data.name,
      data.slug,
      data.type,
      data.collectionDescription,
      data.events.map((event: any) => BalloonEventModel.from(event))
    );
  }
}

export class DynamicEventBuilder extends Builder<
  EventCollectionModel | BalloonEventModel
> {
  private _locationsDates: LocationDates[] = [];
  private _descriptions: Description[] = [];
  private _title: string = '';
  private _tags: string[] = [];
  private _categories: CategoryModel[] = [];
  private _contacts: Contact[] = [];
  private _images: ImageSource[] = [];
  private _organizer: Organizer | null = null;
  private _provider: Provider | null = null;

  public clear(): DynamicEventBuilder {
    this._locationsDates = [];
    this._descriptions = [];
    this._title = '';
    this._tags = [];
    this._categories = [];
    this._contacts = [];
    this._images = [];
    this._organizer = null;
    return this;
  }

  /**
   * Pushes a locationDate to the locationsDates array
   */
  public locationDate(locationDate: LocationDates): DynamicEventBuilder {
    this._locationsDates.push(locationDate);
    return this;
  }

  /**
   * Sets the locationsDates array
   */
  public locationsDates(locationsDates: LocationDates[]): DynamicEventBuilder {
    this._locationsDates = locationsDates;
    return this;
  }

  public descriptions(descriptions: Description[]): DynamicEventBuilder {
    this._descriptions = descriptions;
    return this;
  }

  public title(title: string): DynamicEventBuilder {
    this._title = title;
    return this;
  }

  public tags(tags: string[]): DynamicEventBuilder {
    this._tags = tags;
    return this;
  }

  public categories(categories: CategoryModel[]): DynamicEventBuilder {
    this._categories = categories;
    return this;
  }

  public contacts(contacts: Contact[]): DynamicEventBuilder {
    this._contacts = contacts;
    return this;
  }

  public images(images: ImageSource[]): DynamicEventBuilder {
    this._images = images;
    return this;
  }

  public organizer(organizer: Organizer): DynamicEventBuilder {
    this._organizer = organizer;
    return this;
  }

  public provider(provider: Provider): DynamicEventBuilder {
    this._provider = provider;
    return this;
  }

  public build(): EventCollectionModel | BalloonEventModel {
    if (this._locationsDates.length === 0)
      throw new Error('No locationsDates provided');
    if (this._locationsDates.length === 1)
      return this.buildSingleEvent(this._locationsDates[0]);
    return new EventCollectionModel(
      hashCode(this._title).toString(),
      this._title,
      hashCode(this._title).toString(),
      'serial',
      '',
      this._locationsDates.map((locationDates) =>
        this.buildSingleEvent(locationDates, true)
      )
    );
  }

  public buildSingleEvent(
    locationDates: LocationDates,
    addLocationToTitle: boolean = false
  ): BalloonEventModel {
    const descriptions = [...this._descriptions];
    if (locationDates.notes != '')
      descriptions.push({
        content: locationDates.notes,
        type: DescriptionType.Venues,
        contentType: ContentType.TEXT,
        languageIso: 'de',
      });
    return new BalloonEventModel(
      hashCode(this._title).toString(),
      hashCode(this._title).toString(),
      this._title +
        (addLocationToTitle ? ` - ${locationDates.location.name}` : ''),
      new DescriptionModel(descriptions),
      this._tags.map((tag) => ({ name: tag, slug: tag, id: tag, type: '' })),
      locationDates.location,
      this._categories,
      locationDates.dates,
      this._contacts,
      this._images,
      this._provider,
      new Date(),
      null,
      null,
      this._organizer
    );
  }
}
